// Analytics utility for tracking events
import { logEvent, setUserProperties } from 'firebase/analytics';
import { getFirebaseAnalytics } from './firebase';

// Store events locally for admin dashboard display
const LOCAL_STORAGE_KEY = 'wayo_analytics_events';
const MAX_LOCAL_EVENTS = 500;
const API_URL = window.location.origin;

// Get stored events from localStorage
export function getStoredEvents() {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

// Store event locally for dashboard display
function storeEventLocally(eventName, eventParams = {}) {
  try {
    const events = getStoredEvents();
    const newEvent = {
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      name: eventName,
      params: eventParams,
      timestamp: new Date().toISOString(),
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString()
    };
    events.unshift(newEvent);
    // Keep only the last MAX_LOCAL_EVENTS events
    const trimmedEvents = events.slice(0, MAX_LOCAL_EVENTS);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trimmedEvents));
  } catch (e) {
    console.error('Failed to store event locally:', e);
  }
}

// Send event to backend API for server-side storage
async function sendEventToServer(eventName, eventParams = {}) {
  try {
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        event_name: eventName,
        stall_id: eventParams.stall_id || eventParams.stallId,
        stall_name: eventParams.stall_name || eventParams.stallName,
        category: eventParams.category,
        market: eventParams.market || eventParams.location_market,
        link_type: eventParams.link_type || eventParams.linkType,
        link_category: eventParams.link_category || eventParams.linkCategory,
        params: eventParams
      })
    });
  } catch (error) {
    // Silently fail - don't block user experience
    console.debug('Analytics server tracking failed:', error);
  }
}

// Clear stored events
export function clearStoredEvents() {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
}

// Generic event tracking function
export async function trackEvent(eventName, eventParams = {}) {
  try {
    // Store locally first for dashboard
    storeEventLocally(eventName, eventParams);
    
    // Send to backend server (server-side storage)
    sendEventToServer(eventName, eventParams);
    
    // Send to Firebase Analytics
    const analytics = await getFirebaseAnalytics();
    if (analytics) {
      logEvent(analytics, eventName, {
        ...eventParams,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Analytics tracking error:', error);
  }
}

// Set user properties
export async function setUserProps(properties) {
  try {
    const analytics = await getFirebaseAnalytics();
    if (analytics) {
      setUserProperties(analytics, properties);
    }
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
}

// ============================================
// SPECIFIC EVENT TRACKING FUNCTIONS
// ============================================

// App opened / usage
export function trackAppOpened() {
  trackEvent('app_opened', {
    page: window.location.pathname,
    referrer: document.referrer || 'direct'
  });
}

export function trackAppUsage(duration) {
  trackEvent('app_usage', {
    duration_seconds: duration,
    page: window.location.pathname
  });
}

// Screen/Page views
export function trackScreenView(screenName, screenClass = 'default') {
  trackEvent('screen_view', {
    screen_name: screenName,
    screen_class: screenClass
  });
}

export function trackPageView(pagePath, pageTitle) {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
}

// Authentication events
export function trackLoginSuccess(method = 'email') {
  trackEvent('login_success', {
    method: method,
    timestamp: new Date().toISOString()
  });
}

export function trackLoginFailure(method = 'email', errorMessage = '') {
  trackEvent('login_failure', {
    method: method,
    error_message: errorMessage,
    timestamp: new Date().toISOString()
  });
}

export function trackSignupSuccess(method = 'email') {
  trackEvent('signup_success', {
    method: method,
    timestamp: new Date().toISOString()
  });
}

export function trackSignupFailure(method = 'email', errorMessage = '') {
  trackEvent('signup_failure', {
    method: method,
    error_message: errorMessage,
    timestamp: new Date().toISOString()
  });
}

export function trackLogout() {
  trackEvent('logout', {
    timestamp: new Date().toISOString()
  });
}

// Stall events
export function trackStallViewed(stallId, stallName, category, market = null, tags = []) {
  trackEvent('stall_viewed', {
    stall_id: stallId,
    stall_name: stallName,
    category: category,
    market: market,
    tags: tags.join(', ')
  });
}

export function trackStallCreated(stallId, stallName, createdBy = 'vendor') {
  trackEvent('stall_created', {
    stall_id: stallId,
    stall_name: stallName,
    created_by: createdBy // 'admin' or 'vendor'
  });
}

export function trackStallUpdated(stallId, stallName, updatedBy = 'vendor') {
  trackEvent('stall_updated', {
    stall_id: stallId,
    stall_name: stallName,
    updated_by: updatedBy
  });
}

// Error tracking
export function trackError(errorType, errorMessage, errorStack = '') {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage,
    error_stack: errorStack.substring(0, 500), // Limit stack trace length
    page: window.location.pathname
  });
}

export function trackApiError(endpoint, statusCode, errorMessage) {
  trackEvent('api_error', {
    endpoint: endpoint,
    status_code: statusCode,
    error_message: errorMessage
  });
}

// Backend activity tracking
export function trackBackendActivity(action, endpoint, success = true, details = {}) {
  trackEvent('backend_activity', {
    action: action,
    endpoint: endpoint,
    success: success,
    ...details
  });
}

// Search and filter events
export function trackSearch(searchTerm, resultsCount) {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
}

export function trackFilterApplied(filterType, filterValue) {
  trackEvent('filter_applied', {
    filter_type: filterType,
    filter_value: filterValue
  });
}

// Dashboard events
export function trackDashboardOpened(dashboardType) {
  trackEvent('dashboard_opened', {
    dashboard_type: dashboardType // 'admin' or 'vendor'
  });
}

// Link click events for stall contact and social media
export function trackLinkClick(linkType, linkCategory, stallId = null, stallName = null, locationMarket = null) {
  trackEvent('link_clicked', {
    link_type: linkType, // 'phone', 'whatsapp', 'email', 'website', 'facebook', 'instagram', 'tiktok', 'twitter', 'linkedin', 'directions'
    link_category: linkCategory, // 'contact', 'social_media', or 'directions'
    stall_id: stallId,
    stall_name: stallName,
    location_market: locationMarket // For directions clicks - which market location
  });
}

// Get analytics summary for dashboard
export function getAnalyticsSummary() {
  const events = getStoredEvents();
  const now = new Date();
  const today = now.toLocaleDateString();
  const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const todayEvents = events.filter(e => e.date === today);
  const last7DaysEvents = events.filter(e => new Date(e.timestamp) >= last7Days);
  const last30DaysEvents = events.filter(e => new Date(e.timestamp) >= last30Days);

  // Count by event type
  const countByType = (eventList, eventName) => 
    eventList.filter(e => e.name === eventName).length;

  // Calculate top 10 for stalls, categories, tags, and markets
  const getTop10 = (eventList, paramKey) => {
    const stallViewEvents = eventList.filter(e => e.name === 'stall_viewed');
    const counts = {};
    
    stallViewEvents.forEach(event => {
      const value = event.params?.[paramKey];
      if (value) {
        counts[value] = (counts[value] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  // Get top 10 stalls (by stall_name)
  const topStalls = getTop10(events, 'stall_name');
  
  // Get top 10 categories
  const topCategories = getTop10(events, 'category');
  
  // For tags and markets, we need to track them separately
  // Get top 10 from filter_applied events
  const getTop10Filters = (eventList, filterType) => {
    const filterEvents = eventList.filter(
      e => e.name === 'filter_applied' && e.params?.filter_type === filterType
    );
    const counts = {};
    
    filterEvents.forEach(event => {
      const value = event.params?.filter_value;
      if (value) {
        counts[value] = (counts[value] || 0) + 1;
      }
    });

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const topTags = getTop10Filters(events, 'tag');
  const topMarkets = getTop10Filters(events, 'market');

  return {
    totalEvents: events.length,
    today: {
      total: todayEvents.length,
      appOpened: countByType(todayEvents, 'app_opened'),
      screenViews: countByType(todayEvents, 'screen_view') + countByType(todayEvents, 'page_view'),
      loginSuccess: countByType(todayEvents, 'login_success'),
      loginFailure: countByType(todayEvents, 'login_failure'),
      stallViewed: countByType(todayEvents, 'stall_viewed'),
      stallCreated: countByType(todayEvents, 'stall_created'),
      errors: countByType(todayEvents, 'app_error') + countByType(todayEvents, 'api_error'),
      backendActivity: countByType(todayEvents, 'backend_activity')
    },
    last7Days: {
      total: last7DaysEvents.length,
      appOpened: countByType(last7DaysEvents, 'app_opened'),
      screenViews: countByType(last7DaysEvents, 'screen_view') + countByType(last7DaysEvents, 'page_view'),
      loginSuccess: countByType(last7DaysEvents, 'login_success'),
      loginFailure: countByType(last7DaysEvents, 'login_failure'),
      stallViewed: countByType(last7DaysEvents, 'stall_viewed'),
      stallCreated: countByType(last7DaysEvents, 'stall_created'),
      errors: countByType(last7DaysEvents, 'app_error') + countByType(last7DaysEvents, 'api_error'),
      backendActivity: countByType(last7DaysEvents, 'backend_activity')
    },
    last30Days: {
      total: last30DaysEvents.length,
      appOpened: countByType(last30DaysEvents, 'app_opened'),
      screenViews: countByType(last30DaysEvents, 'screen_view') + countByType(last30DaysEvents, 'page_view'),
      loginSuccess: countByType(last30DaysEvents, 'login_success'),
      loginFailure: countByType(last30DaysEvents, 'login_failure'),
      stallViewed: countByType(last30DaysEvents, 'stall_viewed'),
      stallCreated: countByType(last30DaysEvents, 'stall_created'),
      errors: countByType(last30DaysEvents, 'app_error') + countByType(last30DaysEvents, 'api_error'),
      backendActivity: countByType(last30DaysEvents, 'backend_activity')
    },
    // Top 10 rankings
    topStalls,
    topCategories,
    topTags,
    topMarkets,
    recentEvents: events.slice(0, 50) // Last 50 events for activity feed
  };
}

// Get link click analytics for a specific stall
export function getLinkClickAnalytics(stallId = null, stallName = null) {
  const events = getStoredEvents();
  const now = new Date();
  const today = now.toLocaleDateString();
  const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Filter link_clicked events for this stall
  const linkEvents = events.filter(e => {
    if (e.name !== 'link_clicked') return false;
    if (stallId && e.params?.stall_id !== stallId) return false;
    if (stallName && e.params?.stall_name !== stallName) return false;
    return true;
  });

  const todayEvents = linkEvents.filter(e => e.date === today);
  const last7DaysEvents = linkEvents.filter(e => new Date(e.timestamp) >= last7Days);
  const last30DaysEvents = linkEvents.filter(e => new Date(e.timestamp) >= last30Days);

  // Count clicks by link type
  const countByLinkType = (eventList, linkType) => 
    eventList.filter(e => e.params?.link_type === linkType).length;

  // Count clicks by link type and location
  const countByLinkTypeAndLocation = (eventList, linkType, locationMarket) => 
    eventList.filter(e => e.params?.link_type === linkType && e.params?.location_market === locationMarket).length;

  // Contact details
  const contactTypes = ['phone', 'whatsapp', 'email', 'website'];
  const contactDetails = contactTypes.map(type => ({
    type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    today: countByLinkType(todayEvents, type),
    last7Days: countByLinkType(last7DaysEvents, type),
    last30Days: countByLinkType(last30DaysEvents, type)
  }));

  // Social media
  const socialTypes = ['facebook', 'instagram', 'tiktok', 'twitter', 'linkedin'];
  const socialMedia = socialTypes.map(type => ({
    type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    today: countByLinkType(todayEvents, type),
    last7Days: countByLinkType(last7DaysEvents, type),
    last30Days: countByLinkType(last30DaysEvents, type)
  }));

  // Get directions clicks - collect all unique location markets
  const directionsEvents = linkEvents.filter(e => e.params?.link_type === 'directions');
  const locationMarkets = [...new Set(directionsEvents.map(e => e.params?.location_market).filter(Boolean))];
  
  const directionsClicks = locationMarkets.map(market => ({
    market,
    today: countByLinkTypeAndLocation(todayEvents, 'directions', market),
    last7Days: countByLinkTypeAndLocation(last7DaysEvents, 'directions', market),
    last30Days: countByLinkTypeAndLocation(last30DaysEvents, 'directions', market)
  }));

  // If no specific location data, show total directions clicks
  const totalDirections = {
    type: 'directions',
    label: 'Get Directions',
    today: countByLinkType(todayEvents, 'directions'),
    last7Days: countByLinkType(last7DaysEvents, 'directions'),
    last30Days: countByLinkType(last30DaysEvents, 'directions')
  };

  return {
    contactDetails,
    socialMedia,
    directionsClicks,
    totalDirections,
    totalClicks: {
      today: todayEvents.length,
      last7Days: last7DaysEvents.length,
      last30Days: last30DaysEvents.length
    }
  };
}

// Get ALL link click analytics (for admin dashboard - across all stalls)
export function getAllLinkClickAnalytics() {
  const events = getStoredEvents();
  const now = new Date();
  const today = now.toLocaleDateString();
  const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Filter all link_clicked events
  const linkEvents = events.filter(e => e.name === 'link_clicked');

  const todayEvents = linkEvents.filter(e => e.date === today);
  const last7DaysEvents = linkEvents.filter(e => new Date(e.timestamp) >= last7Days);
  const last30DaysEvents = linkEvents.filter(e => new Date(e.timestamp) >= last30Days);

  // Count clicks by link type
  const countByLinkType = (eventList, linkType) => 
    eventList.filter(e => e.params?.link_type === linkType).length;

  // Contact details
  const contactTypes = ['phone', 'whatsapp', 'email', 'website'];
  const contactDetails = contactTypes.map(type => ({
    type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    today: countByLinkType(todayEvents, type),
    last7Days: countByLinkType(last7DaysEvents, type),
    last30Days: countByLinkType(last30DaysEvents, type)
  }));

  // Social media
  const socialTypes = ['facebook', 'instagram', 'tiktok', 'twitter', 'linkedin'];
  const socialMedia = socialTypes.map(type => ({
    type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    today: countByLinkType(todayEvents, type),
    last7Days: countByLinkType(last7DaysEvents, type),
    last30Days: countByLinkType(last30DaysEvents, type)
  }));

  // Get directions clicks - group by location_market
  const directionsEvents = linkEvents.filter(e => e.params?.link_type === 'directions');
  const locationCounts = {};
  
  directionsEvents.forEach(e => {
    const market = e.params?.location_market || 'Unknown Location';
    if (!locationCounts[market]) {
      locationCounts[market] = { today: 0, last7Days: 0, last30Days: 0 };
    }
    const eventDate = new Date(e.timestamp);
    if (e.date === today) locationCounts[market].today++;
    if (eventDate >= last7Days) locationCounts[market].last7Days++;
    if (eventDate >= last30Days) locationCounts[market].last30Days++;
  });

  // Convert to array and sort by total clicks (last30Days)
  const directionsClicks = Object.entries(locationCounts)
    .map(([market, counts]) => ({
      market,
      ...counts,
      total: counts.last30Days
    }))
    .sort((a, b) => b.total - a.total);

  return {
    contactDetails,
    socialMedia,
    directionsClicks,
    totalClicks: {
      today: todayEvents.length,
      last7Days: last7DaysEvents.length,
      last30Days: last30DaysEvents.length
    }
  };
}
